import { Role } from "@/enum";
import { SORT_BY_ALL } from "@/pages/Private/pages/ToDo/ManageToDos/components/SortDropDown";
import { ShowingMode } from "@/pages/Private/pages/ToDo/ToDoList/enum/ShowingMode.enum";
import { StatisticType } from "@/pages/Private/pages/ToDo/ToDoList/enum/StatisticType.enum";
import { ToDoPriority } from "@/pages/Private/pages/ToDo/ToDoList/enum/ToDoPriority.enum";
import { ToDoStatus } from "@/pages/Private/pages/ToDo/ToDoList/enum/ToDoStatus.enum";
import { QuestionnaireImportance } from "@/pages/Public/pages/Questionnaire/enum/QuestionnaireImportance.enum";

export default {
	translation: {
		marketing: {
			nav: {
				product: "Product",
				pricing: "Pricing",
				about: "About us",
				contact: "Contact",
				login: "Login",
				startCheck: "Start free quick check",
				menu: "Open menu",
			},
			footer: {
				product: "Product",
				pricing: "Pricing",
				quickCheck: "Quick check",
				about: "About us",
				contact: "Contact",
			},
			product: {
				title: "The platform mid-sized companies use to implement NIS-2",
				subtitle:
					"NIS360 turns the NIS-2 requirements into a concrete action plan for your company: prioritized tasks, clear responsibilities, measurable progress and audit-proof evidence.",
				modules: [
					{
						kicker: "Affectedness & GAP analysis",
						title: "Know where you stand – in minutes instead of weeks",
						description:
							"The guided check determines your affectedness and your gaps according to BSIG. The result is not a brochure but your individual action plan – the working basis for your entire implementation.",
					},
					{
						kicker: "Action plan & task board",
						title: "Requirements become tasks, tasks become progress",
						description:
							"30+ prioritized measures with implementation guides, organized on a kanban board with review workflow. Everyone knows what to do – and you always see where you stand.",
					},
					{
						kicker: "Team & roles",
						title: "Compliance is teamwork – NIS360 makes it manageable",
						description:
							"Invite IT, management and data protection with their own roles and permissions. Responsibilities are clearly defined – without email chaos and Excel lists.",
					},
					{
						kicker: "AI assistant",
						title:
							"Questions about implementation? Answered instantly – in the context of your task",
						description:
							"The NIS-2 assistant answers questions right inside the task at hand – trained on BSIG, BSI-Grundschutz and ISO 27001.",
					},
				],
				extraModulesTitle: "Also included in NIS360",
				extraModules: [
					{
						title: "Progress tracking",
						description:
							"Measurable implementation status at a glance: open, ongoing and completed measures – ideal for reports to management.",
					},
					{
						title: "PDF reports & audit evidence",
						description:
							"Audit-proof documentation of your implementation as a PDF – for authorities, auditors and internal committees.",
					},
					{
						title: "Supply chain risk assessment",
						description:
							"Systematically assess risks at service providers and suppliers and meet the NIS-2 requirements for supply chain security.",
					},
				],
				ctaTitle: "See for yourself – free and without obligation",
				ctaDescription:
					"Start with the 3-minute quick check and receive your individual action plan – or get a personal tour of the platform.",
				ctaCheckButton: "Start free quick check",
				ctaDemoButton: "Book a demo",
			},
			pricing: {
				title: "Pricing & plans",
				subtitle:
					"Start for free with the quick check – and upgrade to the full platform when you are ready. For complex requirements, combine NIS360 with consulting by MACONIA GmbH.",
				highlightBadge: "Recommended",
				plans: [
					{
						name: "Quick check",
						price: "Free",
						priceNote: "",
						description: "The risk-free first step: find out where your company stands.",
						features: [
							"Affectedness analysis according to BSIG",
							"GAP overview of your gaps",
							"Personal overview of measures as a PDF",
							"No registration required to start",
						],
						cta: "Check now",
					},
					{
						name: "NIS360 Professional",
						price: "On request",
						priceNote: "",
						description: "The complete platform for your structured NIS-2 implementation.",
						features: [
							"Full action plan (30+ tasks)",
							"Task board with review workflow",
							"Team collaboration with roles & permissions",
							"AI assistant (BSIG, BSI-Grundschutz, ISO 27001)",
							"Audit-proof PDF reports & evidence",
						],
						cta: "Book a demo",
						highlighted: true,
					},
					{
						name: "NIS360 + Consulting",
						price: "Individual",
						priceNote: "",
						description:
							"Platform plus personal support by the IT security experts of MACONIA GmbH.",
						features: [
							"Full access to NIS360 Professional",
							"Personal consulting & workshops",
							"Audit support by experts",
							"Support with policies & processes",
						],
						cta: "Book a call",
					},
				],
				footnote:
					"All plans are based on the same platform – you start with the free quick check and keep your results when upgrading.",
			},
			quickCheck: {
				title: "The free NIS-2 quick check",
				subtitle:
					"Find out in 3 minutes whether your company falls under NIS-2 and where your biggest gaps are. At the end, your personal action plan awaits you in the platform.",
				benefits: [
					"Instant result: affectedness & GAP overview",
					"Personal overview of measures as a PDF",
					"Your individual action plan in the platform – as a free next step",
				],
				ctaButton: "Start quick check",
				stepsTitle: "How the check works",
				steps: [
					{
						step: "Step 1",
						title: "Answer questions",
						description:
							"Guided affectedness and GAP analysis – phrased understandably, without legal jargon.",
					},
					{
						step: "Step 2",
						title: "Instant evaluation",
						description:
							"You see immediately whether your company is affected and where your gaps are.",
					},
					{
						step: "Step 3",
						title: "Receive your action plan",
						description:
							"Your individual plan with 30+ prioritized tasks awaits you in the platform – registration is free.",
					},
				],
				disclaimerTitle: "What the check is not",
				disclaimerText:
					"The quick check is a structured self-assessment and does not replace legal advice. It gives you well-founded orientation based on your answers.",
				privacyTitle: "Your data stays with you",
				privacyText:
					"The check can be started without registration. Your answers are treated confidentially and are not shared with third parties.",
				finalCtaTitle: "Where does your company stand? Find out now.",
			},
			about: {
				title: "Built by practitioners – not by an anonymous SaaS",
				subtitle:
					"NIS360 is developed by MACONIA GmbH – a consulting company specialized in IT security with more than 10 years of experience with mid-sized companies. The platform bundles the knowledge from consulting practice.",
				values: [
					{
						title: "Consulting experience in the product",
						description:
							"More than 10 years of IT security consulting for mid-sized companies flow directly into the measures, guides and workflows of NIS360.",
					},
					{
						title: "Recognized standards",
						description:
							"Consulting and platform follow BSI-Grundschutz, ISO 27001 and the EU requirements – so your implementation stands up to auditors.",
					},
					{
						title: "Partner instead of tool vendor",
						description:
							"When software is not enough, our consultants are there: from workshops and policy work to audit support.",
					},
				],
				certificationsTitle: "Certifications & standards",
				certificationsText:
					"Our work follows established standards and certifications of the IT security industry.",
				consultingTitle: "More than software: consulting when you need it",
				consultingText:
					"Whether audit support, workshops or individual security concepts – the experts behind NIS360 support you personally.",
				consultingCta: "Book a call",
			},
			contact: {
				title: "Contact & demo",
				subtitle:
					"Want to see NIS360 live, or have questions about the platform or your NIS-2 implementation? Write to us – we will get back to you within one business day.",
			},
			privacy: {
				title: "Privacy Policy",
				lastUpdated: "Last updated: June 2026",
				intro:
					"Protecting your personal data is important to us. In this privacy policy we inform you, in accordance with the EU General Data Protection Regulation (GDPR) and the German Federal Data Protection Act (BDSG), about which personal data we collect within the NIS360 platform (nis360.de), for what purposes we process it, and what rights you have.",
				sections: [
					{
						heading: "1. Controller",
						paragraphs: [
							"The controller within the meaning of the GDPR and other data protection provisions is:",
							"MACONIA GmbH\nHolger Könnecke\nKurfürstendamm 11\n10719 Berlin\nGermany",
							"Authorised representative: Holger Könnecke\nCommercial register: HRB 211946 B, Local Court of Berlin\nEmail: datenschutz@maconia.de\nLegal notice: www.maconia.de/impressum",
							"For all questions and requests concerning data protection, as well as to exercise your rights, you can reach us at datenschutz@maconia.de.",
						],
					},
					{
						heading: "2. General information on data processing and legal bases",
						paragraphs: [
							"As a matter of principle, we process the personal data of our users only to the extent necessary to provide a functional platform as well as our content and services. Processing generally takes place only with consent or on the basis of statutory permission.",
							"Where we obtain consent for processing operations, Art. 6(1)(a) GDPR serves as the legal basis. For processing necessary to perform a contract or to carry out pre-contractual measures, Art. 6(1)(b) GDPR is the legal basis. Where processing is necessary to comply with a legal obligation, Art. 6(1)(c) GDPR applies. Where processing is necessary to safeguard our legitimate interests or those of a third party, and the interests, fundamental rights and freedoms of the data subject do not override the former interest, Art. 6(1)(f) GDPR serves as the legal basis.",
							"Personal data is erased or blocked as soon as the purpose of storage ceases to apply. Storage beyond this point only takes place where required by statutory retention obligations.",
						],
					},
					{
						heading: "3. Hosting and server log files",
						paragraphs: [
							"We host our platform with Hetzner Online GmbH, Industriestr. 25, 91710 Gunzenhausen, Germany. The servers are located in data centres within the European Union. A data processing agreement pursuant to Art. 28 GDPR is in place with Hetzner.",
							"Each time the platform is accessed, the system automatically collects data and information from the accessing device. This includes in particular the IP address, the date and time of access, the page or file requested, the previously visited page (referrer), the browser type and version used, and the operating system.",
							"This data is stored in our system log files. The processing serves to deliver the content, ensure the security and stability of the system, and for technical administration. The legal basis is our legitimate interest in a secure and uninterrupted operation pursuant to Art. 6(1)(f) GDPR. The log files are deleted once they are no longer needed or, at the latest, after a defined retention period.",
						],
					},
					{
						heading: "4. Cookies and local storage",
						paragraphs: [
							"We do not use any tracking or marketing cookies, nor any third-party cookies for advertising purposes. We use exclusively technically necessary storage mechanisms in your browser's local storage.",
							"Specifically, after you log in we store an authentication token (JSON Web Token) to keep you signed in during your session, as well as your selected language setting (key i18nextLng). This data remains in your browser and is not evaluated for analytics or advertising purposes.",
							"The legal basis for using these strictly necessary storage mechanisms is Section 25(2) TDDDG (formerly TTDSG) in conjunction with our legitimate interest in providing a functional and secure platform pursuant to Art. 6(1)(f) GDPR. No consent is required for this. You can configure your browser to prevent this storage; in that case, however, the login and language functions may not be available to you.",
						],
					},
					{
						heading: "5. Registration and user account",
						paragraphs: [
							"When you create a user account, we process the data you provide. This includes in particular your first and last name, your email address, information about your company, and your password. Passwords are stored exclusively in encrypted form (hashed using the bcrypt algorithm) and are never accessible to us in plain text.",
							"The processing serves to provide the user account and the contractually agreed functions, and to manage your use of the platform. The legal basis is Art. 6(1)(b) GDPR (performance of a contract or pre-contractual measures).",
							"Within the scope of team collaboration, content you create – such as tasks, comments and status changes – may be visible to authorised members of your company. We also notify you about activity in your account by notification and email.",
							"We use a token-based authentication procedure and, optionally, two-factor authentication (2FA) to secure access to your account. The legal basis is Art. 6(1)(b) and (f) GDPR.",
						],
					},
					{
						heading: "6. NIS-2 quick check and questionnaire",
						paragraphs: [
							"Through the NIS-2 quick check and follow-up questionnaires, we collect information about your company, such as industry, company size, revenue and balance sheet figures, as well as your contact details. This information is used to assess the NIS-2 relevance of your company, to create an individual action plan, and to provide you with the result.",
							"The legal basis is Art. 6(1)(b) GDPR (carrying out pre-contractual measures and performing a contract) and Art. 6(1)(f) GDPR with regard to our legitimate interest in providing and improving our services.",
							"If you provide us with your contact details via the questionnaire or contact form, these may be transferred to our customer relationship management system (see the HubSpot section) in order to follow up on your enquiry.",
						],
					},
					{
						heading: "7. Contact form and getting in touch",
						paragraphs: [
							"If you contact us via the contact form or by email, we process the data you submit – in particular your name, your email address and the content of your message – in order to handle your enquiry.",
							"The legal basis is Art. 6(1)(b) GDPR where your enquiry is aimed at concluding or performing a contract, and otherwise Art. 6(1)(f) GDPR based on our legitimate interest in responding to enquiries. The data is deleted as soon as it is no longer required to achieve the purpose for which it was collected and no statutory retention obligations apply.",
						],
					},
					{
						heading: "8. Sending of emails (Mailjet)",
						paragraphs: [
							"For sending transactional and notification emails (e.g. registration confirmations, password resets, task notifications and replies to contact enquiries) we use the Mailjet service provided by Mailjet SAS (part of the Sinch group), 13-13 bis Rue de l'Aubrac, 75012 Paris, France.",
							"In doing so, your email address and the content required for the respective dispatch are processed. Mailjet processes the data on servers within the European Union. A data processing agreement pursuant to Art. 28 GDPR is in place with the provider.",
							"The legal basis is Art. 6(1)(b) GDPR (performance of a contract) and Art. 6(1)(f) GDPR (legitimate interest in reliable email communication).",
						],
					},
					{
						heading: "9. Customer relationship management (HubSpot)",
						paragraphs: [
							"To manage enquiries and prospects and to maintain our business contacts, we use the CRM system HubSpot. The provider is HubSpot, Inc., 2 Canal Park, Cambridge, MA 02141, USA, represented in the EU by HubSpot Ireland Limited, Ground Floor, Two Dockland Central, Guild Street, Dublin 1, Ireland.",
							"As part of this use, we transfer contact and company data (in particular name, email address and company information) to HubSpot in order to follow up on and process enquiries. The legal basis is Art. 6(1)(f) GDPR (legitimate interest in efficient contact and prospect management) or Art. 6(1)(a) GDPR where you have given your consent.",
							"Processing may also take place on servers in the USA. The transfer to the USA is safeguarded by the conclusion of EU Standard Contractual Clauses pursuant to Art. 46 GDPR; where the provider is certified under the EU-US Data Privacy Framework, the transfer is additionally based on the adequacy decision of the EU Commission. A data processing agreement is in place with the provider.",
						],
					},
					{
						heading: "10. AI assistant (OpenAI)",
						paragraphs: [
							"NIS360 provides an AI-powered assistant that supports you in implementing measures and answers questions about tasks. To provide this function, we use the application programming interface (API) of OpenAI, L.L.C., 3180 18th Street, San Francisco, CA 94110, USA.",
							"When you use the AI assistant, your inputs (chat messages), the relevant task context and – if you upload documents – their content are transmitted to and processed by OpenAI in order to generate a response. According to OpenAI, data transmitted via the API is not used to train its models.",
							"Please do not enter any personal or sensitive data into the AI assistant that is not required for the respective request. The legal basis is Art. 6(1)(b) GDPR (provision of the contractually agreed function) and Art. 6(1)(f) GDPR (legitimate interest in a powerful support function).",
							"Processing may take place on servers in the USA. The transfer is safeguarded by EU Standard Contractual Clauses pursuant to Art. 46 GDPR. A data processing agreement pursuant to Art. 28 GDPR is in place with OpenAI.",
						],
					},
					{
						heading: "11. Web analytics (Plausible Analytics)",
						paragraphs: [
							"For the statistical analysis of the use of our platform, we use Plausible Analytics, a privacy-friendly web analytics service provided by Plausible Insights OÜ, Västriku tn 2, Tartu, Estonia. Data processing takes place entirely within the European Union.",
							"Plausible does not use cookies and does not store any personal data. No cross-device profiles are created. Information collected, such as the pages visited, approximate origin, device used and the source of the visit, is evaluated exclusively in aggregated and anonymised form; IP addresses are not stored permanently and are not used for identification purposes.",
							"The legal basis is our legitimate interest in a data-minimising analysis to improve our offering pursuant to Art. 6(1)(f) GDPR.",
						],
					},
					{
						heading: "12. Document upload and AI-supported processing",
						paragraphs: [
							"If you upload documents within the platform, they are stored on an EU-based object storage operated by us. To provide AI-supported functions, document contents may be converted into machine-readable vectors (so-called embeddings) and stored in a vector database operated by us, in order to make relevant content retrievable for the AI assistant.",
							"The processing serves exclusively to provide the functions you use. The legal basis is Art. 6(1)(b) GDPR. The service named in section 10 (OpenAI) may be used to generate the embeddings.",
						],
					},
					{
						heading: "13. Recipients and processors",
						paragraphs: [
							"To provide our services, we use carefully selected service providers who process personal data on our behalf. We have concluded data processing agreements pursuant to Art. 28 GDPR with these processors.",
							"These are in particular: Hetzner Online GmbH (hosting, EU), Mailjet SAS (email dispatch, EU), HubSpot, Inc. (CRM, EU/USA) and OpenAI, L.L.C. (AI functions, USA). A transfer to further third parties only takes place where this is legally permissible or necessary for the performance of a contract, or where you have given your consent.",
						],
					},
					{
						heading: "14. Data transfers to third countries",
						paragraphs: [
							"A transfer of personal data to countries outside the EU or EEA (in particular to the USA, in connection with OpenAI and HubSpot) only takes place in compliance with the statutory requirements. Where there is no adequacy decision by the EU Commission, we base the transfer on EU Standard Contractual Clauses pursuant to Art. 46(2)(c) GDPR and, where applicable, supplementary protective measures. Where a provider is certified under the EU-US Data Privacy Framework, the transfer is additionally based on the corresponding adequacy decision.",
						],
					},
					{
						heading: "15. Storage period and erasure",
						paragraphs: [
							"We process and store personal data only for as long as is necessary to achieve the respective processing purposes or as required by statutory retention periods (e.g. commercial and tax law obligations). Once the purpose ceases to apply and any retention periods have expired, the data is erased or blocked. When you delete your user account, the associated data is erased, unless retention obligations stand in the way.",
						],
					},
					{
						heading: "16. Your rights as a data subject",
						paragraphs: [
							"You have the following rights vis-à-vis us with regard to the personal data concerning you: the right of access (Art. 15 GDPR), rectification (Art. 16 GDPR), erasure (Art. 17 GDPR), restriction of processing (Art. 18 GDPR), data portability (Art. 20 GDPR), and the right to object to processing (Art. 21 GDPR).",
							"To exercise your rights, an informal message to datenschutz@maconia.de is sufficient.",
						],
					},
					{
						heading: "17. Withdrawal of consent",
						paragraphs: [
							"Where the processing of your data is based on consent, you have the right to withdraw it at any time with effect for the future (Art. 7(3) GDPR). The lawfulness of the processing carried out up to the point of withdrawal remains unaffected.",
						],
					},
					{
						heading: "18. Right to lodge a complaint with a supervisory authority",
						paragraphs: [
							"Without prejudice to any other administrative or judicial remedy, you have the right to lodge a complaint with a data protection supervisory authority if you consider that the processing of the data concerning you infringes the GDPR.",
							"The authority responsible for us is: Berliner Beauftragte für Datenschutz und Informationsfreiheit, Alt-Moabit 59-61, 10555 Berlin, Germany. However, you may also contact the supervisory authority of your usual place of residence or work.",
						],
					},
					{
						heading: "19. Data security",
						paragraphs: [
							"We take technical and organisational measures to protect your data against loss, destruction, manipulation and unauthorised access. Transmission is encrypted via TLS (Transport Layer Security). Passwords are stored exclusively as a cryptographic hash, and access to data is restricted by access and authorisation concepts. Our measures are continuously adapted in line with technological developments.",
						],
					},
					{
						heading: "20. Changes to this privacy policy",
						paragraphs: [
							"We reserve the right to adapt this privacy policy so that it always complies with current legal requirements or in order to reflect changes to our services. The current version applies to your next visit.",
						],
					},
				],
			},
		},
		"set-password": {
			secondTitle: "Set your password",
			title: "Welcome back {{name}}",
			description: "Your NIS-2-Compliance Roadmap is ready to download",
			confirm: "Confirm",
			pdfTitle: "NIS-2-Compliance Roadmap PDF",
			pdfSubtitle: "Your complete task list with over 30 structured compliance tasks",
			pdfContent: "Contents of the PDF:",
			pdfItemTasks: "Complete task list (30+ tasks)",
			pdfItemDescriptions: "Detailed descriptions and instructions",
			pdfItemChecklists: "Compliance checklists",
			downloadPdf: "Download PDF now",
			formDescription: "Get access to the NIS360 app and manage your compliance tasks.",
			showPassword: "Show password",
			hidePassword: "Hide password",
			passwordMin8: "Password must contain at least 8 characters",
			passwordNumber: "Password must contain at least 1 number",
			passwordSpecial: "Password must contain at least 1 special character",
			passwordUppercase: "Password must contain at least 1 uppercase letter",
			passwordLowercase: "Password must contain at least 1 lowercase letter",
		},
		companies: {
			title: "Company Management",
			subtitle: "Manage your teams and user permissions.",
			search: "Search for company",
			"search-by-organization": "Search by organization",
			"search-by-organization-description": "View and manage a list of companies and their teams.",
			name: "Name",
			linkedinUrl: "LinkedIn URL",
			users: "Users",
			website: "Website",
			create: {
				button: "Add new company",
			},
			add: {
				title: "Add new company",
				subtitle: "Fill in the required fields to add a new company to the system.",
			},
			edit: {
				title: "Edit new company",
				subtitle: "Fill in the required fields to edit the company.",
			},
			delete: {
				title: "Delete this company?",
				description: 'Are you sure you want to delete "{{companyName}}"?',
			},
		},
		company: {
			title: "Company Management",
			subtitle: "Manage your team members and their account permissions here.",
			tabs: {
				general: "General",
				user: "Users",
				roles: "User roles",
			},
			companyInformation: "Company Information",
			autoInvite: "Auto Invite to your company linkedin page",
			apolloApiKey: "Apollo API Key",
			lemlistApiKey: "Lemlist API Key",
			"invite-company-members": "Invite company members",
			"invite-company-members-description": "Invite your colleagues to collaborate.",
			"add-another": "Add another",
			"send-invite": "Send invite",
			"company-members": "Company members",
			"company-member-description": "Manage your existing team and change roles/permissions.",
		},
		dashboard: {
			title: "Welcome",
		},
		questionnaire: {
			title: "Questionnaire",
			"select-industry": "Select an industry",
			landing: {
				title: "Implement NIS-2. Structured, provable, revision-proof.",
				subtitle:
					"NIS360 creates your individual action plan and guides your team from the affectedness analysis to audit-proof documentation – all in one platform.",
				ctaPrimary: "Start for free – with the 3-minute quick check",
				ctaSecondary: "View the platform",
				ctaNote: "Free · no registration required to start · instant result",
				yes: "Yes",
				no: "No",
				idk: "Check affectedness",
				startCheck: "Start Check",
			},
			landingThreeColumns: {
				kicker: "Typical scenarios",
				title: "Does this sound familiar?",
				subtitle:
					"Three situations where Excel lists and gut feeling are no longer enough – and how NIS360 solves them.",
				problemLabel: "The problem",
				solutionLabel: "The solution in NIS360",
				features: [
					{
						title: "NIS-2 relevance",
						description:
							"Many companies do not know whether the NIS-2 directive applies to them. A wrong assessment leads to unnecessary effort – or heavy fines.",
						module: "Affectedness & GAP analysis",
						solution:
							"The free quick check shows you within 3 minutes whether you are affected – and turns the result into your individual action plan.",
					},
					{
						title: "Legal requirements",
						description:
							"Reporting and documentation obligations as well as security measures can be complex. Even small implementation mistakes can lead to sanctions and fines.",
						module: "Action plan & task board",
						solution:
							"NIS360 translates legislation into to-dos – every requirement becomes a concrete task with priorities, owners and audit-proof evidence.",
					},
					{
						title: "Supply chain risks",
						description: "A security incident at a single partner can endanger the whole company.",
						module: "Supply chain risk assessment",
						solution:
							"NIS360 systematically assesses risks in your supply chain and provides concrete instructions for mutual protection.",
					},
				],
			},
			landingProductShowcase: {
				title: "One platform for your entire NIS-2 implementation",
				description:
					"From the first check to the audit evidence: NIS360 turns abstract requirements into concrete work your team completes step by step.",
				features: [
					{
						kicker: "INDIVIDUAL ACTION PLAN",
						title: "Your analysis automatically becomes 30+ concrete, prioritized tasks",
						description:
							"Every task comes with an understandable implementation guide – you always know what to do next and why.",
					},
					{
						kicker: "PROGRESS & RESPONSIBILITIES",
						title: "Everyone knows what to do. You always see where you stand.",
						description:
							"Kanban board, priorities and review workflow: tasks get assigned, progress is measurable – and can be shown to the auditor at any time.",
					},
					{
						kicker: "TEAM COLLABORATION",
						title: "Bring IT, management and data protection to one table",
						description:
							"Invite your team with roles and permissions. Responsibilities are clearly defined – without email chaos and Excel lists.",
					},
					{
						kicker: "AI ASSISTANT",
						title:
							"Questions about implementation? Answered instantly – in the context of your task",
						description:
							"The NIS-2 assistant knows your current task and gives well-founded answers – trained on BSIG, BSI-Grundschutz and ISO 27001.",
					},
				],
			},
			landingStats: {
				items: [
					{ value: "30+", label: "concrete measures in your plan" },
					{ value: "3 min.", label: "to your first result" },
					{ value: "Made in Germany", label: "hosted in a German data center" },
					{ value: "BSIG · ISO 27001", label: "the platform's expert foundation" },
				],
			},
			landingRisk: {
				kicker: "What's at stake",
				title: "NIS-2 is not a recommendation – it's a duty with consequences",
				subtitle:
					"The directive is in force. Those who fail to act risk severe fines, personal liability and reputational damage.",
				items: [
					{
						title: "Fines up to €10M",
						description:
							"Violations can trigger fines of up to €10M or 2% of global annual turnover – whichever is higher.",
					},
					{
						title: "Personal management liability",
						description:
							"Management must approve and oversee risk measures – and is personally liable for failures. This duty cannot be delegated.",
					},
					{
						title: "The deadline has already passed",
						description:
							"NIS-2 has applied across the EU since October 2024. Authorities can review your scope, measures and evidence at any time.",
					},
				],
			},
			landingAffected: {
				kicker: "Am I affected?",
				title: "NIS-2 affects far more companies than you'd think",
				subtitle:
					"Companies meeting at least two of these thresholds usually fall under NIS-2 – often also as a supplier to an affected company.",
				criteria: [
					{ value: "50+", label: "employees (medium & large companies)" },
					{ value: "€10M +", label: "annual turnover or balance sheet total" },
					{ value: "18 sectors", label: "incl. energy, health, transport, IT, food" },
				],
				note: "Smaller companies can also be affected as part of the supply chain – e.g. as a service provider or supplier.",
				ctaTitle: "Not sure whether NIS-2 applies to you?",
				ctaButton: "Check your scope in 3 minutes",
			},
			landingComparison: {
				kicker: "Why NIS360",
				title: "NIS360 compared to the alternatives",
				subtitle:
					"Spreadsheets, expensive one-off consulting or generic GRC tools – here's how NIS360 stacks up.",
				featureHeader: "Requirement",
				columns: ["Excel & email", "External consulting", "Generic GRC tool", "NIS360"],
				highlightLabel: "Recommended",
				rows: [
					{ feature: "NIS-2-specific action plan", values: ["no", "yes", "no", "yes"] },
					{ feature: "Ready to go instantly (3 min.)", values: ["no", "no", "no", "yes"] },
					{
						feature: "Predictable cost instead of day rates",
						values: ["partial", "no", "yes", "yes"],
					},
					{
						feature: "Audit-proof evidence at the push of a button",
						values: ["no", "partial", "partial", "yes"],
					},
					{
						feature: "Team collaboration with roles & permissions",
						values: ["no", "no", "partial", "yes"],
					},
					{ feature: "AI assistant for implementation", values: ["no", "no", "no", "yes"] },
					{
						feature: "Continuously up to date at no extra cost",
						values: ["no", "no", "partial", "yes"],
					},
				],
			},
			landingSecurity: {
				kicker: "Security & data protection",
				title: "Your data stays in Germany",
				description:
					"A cybersecurity platform must meet the highest standards itself. NIS360 runs entirely in a German data center – your data never leaves Germany.",
				items: [
					{
						title: "Hosted in Germany",
						description:
							"Operated in a data center in Nuremberg – no data transfer to third countries.",
					},
					{
						title: "GDPR-compliant processing",
						description:
							"Processing in line with GDPR requirements. Data processing agreement available on request.",
					},
					{
						title: "Encrypted transmission",
						description: "All connections are encrypted end-to-end via TLS.",
					},
				],
			},
			landingLeadMagnet: {
				title: "Where does your company stand? Find out in 3 minutes.",
				bullets: [
					"Free and no registration required to start",
					"Instant result: affectedness and gaps at a glance",
					"Your personal overview of measures as a PDF",
				],
				ctaButton: "Start quick check",
				registerHint: "Already convinced?",
				registerLink: "Register directly",
			},
			landingWhyNIS2MattersSection: {
				title: "Why you shouldn’t ignore NIS-2",
				description:
					"NIS-2 introduces new cybersecurity requirements for companies – many medium-sized enterprises are now subject to reporting and implementation obligations for the first time. Those who don’t prepare risk fines and reputational damage.",
				features: [
					{
						alt: "Fines icon",
						text: "Up to €10 million or 2% annual revenue fine",
					},
					{
						alt: "SME icon",
						text: "Also applies to many SMEs and suppliers",
					},
					{
						alt: "Organizational measures icon",
						text: "Obligation to implement technical & organizational measures",
					},
					{
						alt: "Incident reporting icon",
						text: "Mandatory reporting of security incidents",
					},
				],
				imageAlt: "Why NIS-2 matters",
			},
			landingHowItWorksSection: {
				title: "Four steps to provable compliance",
				description:
					"The free quick check is step 1 – it generates your individual action plan in the platform.",
				steps: [
					{
						step: "Step 1",
						title: "Quick check",
						description: "3 minutes, free: affectedness and GAP analysis for your company",
						badgeColor: "#FFEDE6",
					},
					{
						step: "Step 2",
						title: "Receive your action plan",
						description:
							"Your individual roadmap with 30+ prioritized tasks and implementation guides",
						badgeColor: "#FEEAA3",
					},
					{
						step: "Step 3",
						title: "Implement, structured",
						description:
							"Assign tasks to your team, track progress, use AI help right inside each task",
						badgeColor: "#E8E6E2",
					},
					{
						step: "Step 4",
						title: "Prove & pass",
						description:
							"Audit-proof documentation for authorities and auditors – exportable at any time",
						badgeColor: "#DAF8E6",
						imgWidth: 100,
						imgHeight: 88,
					},
				],
				startCheckButton: "Start with step 1 – the quick check",
				arrowLeftAlt: "Arrow left",
			},
			landingContactSection: {
				title: "Contact us",
				description: "We're here for you – with questions or a message. Contact us easily.",
				success: "Your message has been sent successfully. We will get back to you.",
				form: {
					title: "Contact us",
					description: "Just send us a message. We will get back to you.",
					confirm: "Confirm",
				},
				name: "Name",
				namePlaceholder: "Enter your name",
				emailPlaceholder: "Enter your email address",
				messagePlaceholder: "Enter your message",
				email: "Email address",
				message: "Message",
			},
			landingExpertsSection: {
				title: "Your Experts for NIS-2 Compliance",
				description:
					"MACONIA specializes in IT security consulting for medium-sized companies. We help you implement the requirements of the NIS-2 directive legally compliant and pragmatically.",
				features: [
					{
						alt: "rating",
						text: "> 10 years of experience in IT security consulting",
					},
					{
						alt: "expertise",
						text: "Consulting according to BSI basic protection, ISO 27001, and EU regulations",
					},
				],
				dataPrivacy: {
					title: "Built by practitioners, designed for practice. From the field, for the field.",
					description:
						"NIS360 grew out of the day-to-day consulting practice of MACONIA GmbH. All features follow standardized approaches that can be used for every NIS-2 project.",
					padlockAlt: "Padlock",
				},
			},
			landingTestimonials: {
				testimonials: [
					{
						quote:
							"MACONIA helped us implement the NIS-2 requirements in a structured and efficient way. Highly recommended for medium-sized businesses.",
						name: "Gabrielle Williams",
						title: "Managing Director, Technology Company",
					},
					{
						quote:
							"Professional support from start to finish. Our security requirements were fully met.",
						name: "Lukas Steiner",
						title: "IT Manager, Financial Services",
					},
					{
						quote: "Thanks to MACONIA, we met the compliance requirements on time. Top consulting!",
						name: "Anja Richter",
						title: "CEO, Industrial Company",
					},
				],
				quoteAlt: "Quote icon",
				avatarAlt: "Avatar",
				sliderLeftAlt: "Slider left",
				sliderRightAlt: "Slider right",
			},
			landingCTASection: {
				ctaTitle: "Check NIS-2 relevance now – free & in just 3 minutes",
				ctaButton: "Start NIS-2 check",
				footerAlt: "Footer illustration",
				imprint: "Imprint",
				privacyPolicy: "Privacy policy",
				copyright: "Copyright © 2026 MACONIA GmbH. All rights reserved.",
			},
			start: {
				title: "Assess your cybersecurity according to NIS-2!",
				"get-started": "Get started",
				"description-first":
					"The European NIS-2 Directive sets new cybersecurity requirements for companies in the public and critical sectors. You need to complete an assessment to check your compliance and receive a list of key actions to improve your security.",
				"description-first-highlight": "NIS-2 Directive",
				"description-second": "This survey will help you:",
				"description-second-list": {
					"1": "Identify vulnerabilities in your security system;",
					"2": "Receive personalized recommendations for NIS-2 compliance;",
					"3": "Create a to-do list to enhance your cybersecurity;",
				},
				"description-third":
					"The process takes just a few minutes. Please provide honest answers to get the most accurate analysis.",
				"description-third-highlight": "a few minutes",
				"compliance-question": "Do you need to comply with NIS-2?",
				yes: "Yes",
				no: "No",
				idk: "I don't know",
			},
			progress: {
				back: "Back",
				next: "Next",
				finish: "Get the results",
				readLess: "Read less",
				readMore: "Read more",
				step: "Step {{currentStep}} of {{totalLength}}",
				subStep: "Sub-step {{currentStep}} of {{totalLength}}",
			},
			finalize: {
				introduction: {
					title: "Das wichtigste auf einen Blick",
				},
				continue: "Continue",
				"back-to-start": "Back to start",
				"to-registration": "To registration",
				title: "Your NIS-2 compliance roadmap",
				subtitle: "We have created a personalized roadmap to ensure your compliance with NIS-2.",
				overallComplianceLevel: "Overall level of compliance:",
				"overallComplianceLevel-description":
					"Based on your responses, your company meets some NIS-2 requirements but needs improvements in key areas to ensure full compliance. Below is your personalized action plan.",
				"overallComplianceLevel-description-not-needed":
					"Based on the survey results, your organization does not require compliance with NIS-2 requirements. This means that, for now, you do not need to meet the cybersecurity requirements outlined by this directive.",
				"critical-gaps": "Critical security gaps (Very Important) - {{count}}",
				"key-improvements": "Key security improvements needed (Important) - {{count}}",
				"next-steps": "Next steps:",
				"next-steps-description":
					"To view a detailed personalized action plan (to-do list), track progress on each task, and access recommendations, please register.",
				"next-steps-description-highlight": "please register.",
				[QuestionnaireImportance.IMPORTANT]: "Important",
				[QuestionnaireImportance.NONE]: "Not considered by NIS-2",
				[QuestionnaireImportance.STOP]: "Not have to comply with NIS-2.",
				"first-pass-questionnaire":
					"To register, you first need to complete a questionnaire to determine the level of criticality of your company",
				"to-questionnaire": "To questionnaire",
				"company-registration": "Company registration",
				"questionnaire-required": "Completion of the questionnaire is required",
				planPreview: {
					title: "Preview: your personal action plan",
					visibleCount: "3 of 32 measures visible",
					visibleTasks: [
						{
							title: "Conduct a risk analysis for critical systems",
							category: "Risk management",
						},
						{
							title: "Set up reporting channels for security incidents (24h deadline)",
							category: "Incident response",
						},
						{
							title: "Define responsibilities for information security",
							category: "Governance",
						},
					],
					blurredTasks: [
						{
							title: "Implement multi-factor authentication for critical accounts",
							category: "Technical measures",
						},
						{
							title: "Document a backup and recovery concept",
							category: "Business continuity",
						},
						{
							title: "Create a training plan for management",
							category: "Awareness",
						},
					],
					overlayText:
						"Register for free to see your full plan with all tasks and implementation guides.",
				},
			},
			cybersecurity: {
				title: "Do you still want to check your cybersecurity?",
				description:
					"Even if you're not required to comply with NIS-2, understanding your cybersecurity level can still be beneficial.",
				yes: "Yes",
			},
			business: {
				industry: {
					title: "What's your industry",
					description: "Select the industry your company primarily operates in.",
				},
				size: {
					title: "What's your company size?",
					description: "Choose your company's size based on the number of employees.",
				},
				turnover: {
					title: "What's your annual turnover?",
					description: "Estimate your company's yearly revenue.",
				},
				balance: {
					title: "What's your annual balance?",
					description: "In which range is your annual balance?",
				},
				progress: {
					back: "Back",
					next: "Next",
					finish: "Finish",
					step: "Step {{currentStep}} of {{totalLength}}",
					subStep: "Sub-step {{currentStep}} of {{totalLength}}",
				},
			},
			"needs-analysis": {
				progress: {
					back: "Back",
					next: "Next",
					finish: "Finish",
					step: "Step {{currentStep}} of {{totalLength}}",
				},
				finalize: {
					continue: "Continue",
					"back-to-start": "Back to start",
					"to-registration": "To registration",
					title: "Your NIS-2 compliance results!",
					overallComplianceLevel: "Overall level of compliance:",
					"overallComplianceLevel-description":
						"Based on your responses, your company meets some NIS-2 requirements but needs improvements in key areas to ensure full compliance. Below is your personalized action plan.",
					"overallComplianceLevel-description-not-needed":
						"Based on the survey results, your organization does not require compliance with NIS-2 requirements. This means that, for now, you do not need to meet the cybersecurity requirements outlined by this directive.",
					"critical-gaps": "Critical security gaps (Very Important) - {{count}}",
					"key-improvements": "Key security improvements needed (Important) - {{count}}",
					"next-steps": "Next steps:",
					"next-steps-description":
						"To view a detailed personalized action plan (to-do list), track progress on each task, and access recommendations, please register.",
					"next-steps-description-highlight": "please register.",
					[QuestionnaireImportance.IMPORTANT]: "Important",
					[QuestionnaireImportance.NONE]: "Not considered by NIS-2",
					[QuestionnaireImportance.STOP]: "Not have to comply with NIS-2.",
					"first-pass-questionnaire":
						"To register, you first need to complete a questionnaire to determine the level of criticality of your company",
					"to-questionnaire": "To questionnaire",
					"company-registration": "Company registration",
					"questionnaire-required": "Completion of the questionnaire is required",
					"self-assessment-text":
						"Based on your answers, your company <1>must comply with the NIS-2 directive</1>. The next step is a self-assessment. Click the button below to begin the assessment.",
					"cybersecurity-prompt-text":
						"Your company <1>is not required to comply with the NIS-2 directive</1>. However, do you still want to check your cybersecurity status?",
					"start-self-assessment": "Start self-assessment",
					"check-cybersecurity": "Yes",
				},
			},
			"self-assessment": {
				yes: "Yes",
				no: "No",
				skip: "Skip",
				progress: {
					back: "Back",
					next: "Next",
					finish: "Finish",
					step: "Step {{currentStep}} of {{totalLength}}",
					subStep: "Sub-step {{currentStep}} of {{totalLength}}",
				},
				finalize: {
					title: "Self-Assessment Complete",
					"summary-text":
						"You’ve completed the self-assessment. Your responses have been reviewed and the necessary action items will be created during registration.",
					"proceed-to-registration": "Proceed to Registration",
				},
			},
		},
		"to-do": {
			QA: "QA",
			none: "None",
			"cannot-move-subtask-to-done":
				"Only subtasks in the QA column can be moved to the Done column",
			"cannot-move-task-to-done": "Only tasks in the QA column can be moved to the Done column",
			"show-less": "Show less",
			"show-more": "Show all categories",
			"overall-progress": "Overall Progress",
			"tasks-completed": "Tasks Completed",
			priority: "Priority",
			search: "Search for tasks...",
			title: "To Do List",
			all: "All",
			"i-see": "I see",
			subTask: "Subtask",
			"qa-assignee": "Assignee change for QA",
			"qa-assignee-task": "You must choose the person responsible for QA phase of this task",
			"qa-assignee-subtask": "You must choose the person responsible for QA phase of this subtask",
			recommendation: "Recommendation",
			requirement: "Requirement",
			"date-created": "Date Created:",
			"list-empty": "List is empty",
			"change-task-status": "Change the task status to ",
			category: "Category:",
			[ToDoStatus.TO_DO]: "To Do",
			[ToDoStatus.IN_PROGRESS]: "In Progress",
			[ToDoStatus.DONE]: "Done",
			[ToDoPriority.LOW]: "Low",
			[ToDoPriority.MEDIUM]: "Medium",
			[ToDoPriority.HIGH]: "High",
			statistic: {
				[StatisticType.TOTAL]: "Total tasks",
				[StatisticType.REMAINING]: "Remaining tasks",
				[StatisticType.COMPLETED]: "Completed tasks",
			},
			subTasks: "Sub-tasks",
			subtaskProgress: "{{done}} of {{total}} done",
			activity: "Activity",
			comments: "Comments",
			historyTab: "History",
			noComments: "Nothing said yet.",
			noHistory: "No changes recorded yet.",
			moveTo: "Move to",
			created: "Created",
			updated: "Updated",
			backToTask: "Back to task",
			unsaved: {
				badge: "Unsaved",
				title: "Unsaved changes",
				description: "You have changes that have not been saved yet. What would you like to do?",
				keepEditing: "Keep editing",
				discard: "Discard",
				saveAndClose: "Save & continue",
			},
			activityLog: {
				unknownUser: "Unknown",
				entityTask: "the task",
				entitySubtask: "the subtask",
				empty: "—",
				textChanged: "changed",
				fields: {
					title: "Title",
					description: "Description",
					status: "Status",
					priority: "Priority",
					complexityEstimate: "Complexity",
					tagId: "Tag",
					assigneeId: "Assignee",
				},
				HISTORY: {
					CREATE: "{{user}} created {{entity}}",
					UPDATE: "{{user}} updated {{entity}}",
					DELETE: "{{user}} deleted {{entity}}",
				},
				COMMENT: {
					CREATE: "{{user}} added a comment",
					UPDATE: "{{user}} edited a comment",
					DELETE: "{{user}} deleted a comment",
				},
			},
			description: "Description",
			onYourMind: "What's on your mind?",
			addComment: "Add a comment...",
			untagged: "Untagged",
			inherited: "Inherited",
		},
		aiChat: {
			toggle: "AI Assistant",
			title: "NIS-2 AI Assistant",
			clearHistory: "Clear history",
			loading: "Loading...",
			emptyState: "Ask me anything about NIS-2 compliance and this task.",
			placeholder: "Ask about NIS-2...",
		},
		createTask: {
			deleteTask: "Delete task",
			deleteSubtask: "Delete subtask",
			createTask: "Create task",
			createSubtask: "Create subtask",
			subtaskOf: "Subtask of",
			expandSubtasks: "Expand subtasks",
			collapseSubtasks: "Collapse subtasks",
			mainTitle: "Create New Task",
			title: "Title",
			save: "Save",
			filterByAssignee: "All assignees",
			description: "Description",
			assignee: "Assignee",
			priority: "Priority",
			status: "Status",
			complexity: "Complexity",
			tags: "Tag",
			unassigned: "Unassigned",

			low: "Low",
			medium: "Medium",
			high: "High",

			"in-progress": "In progress",
			done: "Done",

			easy: "Easy",
			normal: "Normal",
			hard: "Hard",
			"very-hard": "Very hard",

			validation: {
				"title-required": "Title is required",
			},
		},

		sidebar: {
			users: "Users",
			roles: "User roles",
			logout: "Logout",
			accounts: "Accounts",
			settings: "Settings",
			accountSettings: "Account Settings",
			profile: "My Profile",
			companies: "Company Management",
			back: "Back to your account",
			all: "All",
			cybersecurityIncidentBasics: "Cybersecurity Incident Basics",
			company: "Company",
			menu: "Menu",
			"my-profile": "My Profile",
			"to-do-list": "To-Do List",
			"swimlane-board": "Swimlane Board",
			team: "Team",
			"manage-to-do": "Manage To-Do",
			"manage-questionnaire": "Questionnaire",
			"manage-industries": "Industries",
			"manage-company-size": "Company Sizes",
			"manage-company-revenue": "Company Revenues",
			"manage-task-templates": "Task Templates",
			admin: "Admin",
		},
		errors: {
			requiredFields: "Not all necessary fields are filled.",
			expectedNumber: "Number expected / enter 0 if not needed.",
			customColumnAlreadyExists: "Custom column already exists.",
			invalidEmail: "Invalid email",
			invalidPassword: "Invalid password",
			passwordGuidelinesNotMet: "Password guidelines not met",
			termsAcceptanceMissing: "Terms acceptance missing",
			invalidValue: "Invalid value",
			invalidData: "Invalid data",
			userNotConfirmed: "User not confirmed",
			passwordMismatch: "Passwords do not match",
			invalidToken: "Invalid token",
			emailAlreadyTaken: "Email already taken",
			wentWrong: "Something went wrong",
			unauthorized: "Unauthorized",
			invalidAuthCode: "Invalid auth code",
			nameMissing: "Name missing",
			wrongUrlShouldBeLinkedinProfile: "Wrong URL. Should be a LinkedIn profile URL",
			notEnoughCredits: "Not enough credits",
			notAcceptedCompany: "Your company has not been confirmed yet",
			notConfirmedEmail: "Your email has not been confirmed yet",
			tokenWasUsed: "Already confirmed",
			answersRequired: "The questionnaire must have at least one answer",
		},
		notifications: {
			panel: {
				title: "Notifications",
				markAllRead: "Mark all read",
				tabs: {
					all: "All",
					unread: "Unread",
				},
				allCategories: "All categories",
				categories: {
					assignment: "Assignment",
					status: "Status",
					comment: "Comment",
					tag: "Tag",
					system: "System",
				},
				loading: "Loading…",
				empty: "No notifications",
				allCaughtUp: "You're all caught up!",
				previous: "← Previous",
				next: "Next →",
				pageOf: "Page {{page}} of {{totalPages}}",
			},
			item: {
				fallbackTitle: "Notification",
				aggregatedItems: "{{count}} items",
			},
			toast: {
				fallbackTitle: "Notification",
			},
			preferences: {
				title: "Notification Preferences",
				subtitle: "Configure which notifications you receive and how.",
				headers: {
					group: "Group",
					inApp: "In-app",
					email: "Email",
					digest: "Summary",
					digestTooltip:
						"When enabled, you receive a single bundled overview of all notifications in this category — sent at the interval you choose in the Frequency column — instead of an individual notification for every event. Use this if you want to stay informed without being interrupted by every update. The Frequency column controls how often the summary is delivered (daily or weekly). If Frequency is set to Instant or Off, no summary is sent.",
					frequency: "Frequency",
				},
				frequencyOptions: {
					instant: "Instant",
					daily: "Daily",
					weekly: "Weekly",
					off: "Off",
				},
				loading: "Loading preferences…",
				resetToDefaults: "Reset to defaults",
			},
		},
		team: {
			title: "Team",
		},
		account: {
			connectionScraping: "LinkedIn-Connections",
			enableConnectionScraping: "active the scraping of your linkedin connections",
			enableConnectionScrapingDescription:
				"This allows you to identify which prospects you or your colleagues are already connected to on Linkedin.",
			linkedinAutomation: "LinkedIn Automation",
			enableAutoAccept: "Enable Auto Accept",
			enableAutoAcceptDescription: "Automatically accept connection requests",
			profileVisits: "Profile Visits",
			warningPremiumAccountRequired: "This feature is only available for premium accounts",
			enableProfileVisitsScraping: "Enable Profile Visits Scraping",
			changePassword: "Change password",
			currentPassword: "Current password",
			disableTwoFactorAuth: "Disable 2FA",
			enableTwoFactorAuth: "Enable 2FA",
			enableTwoFactorAuthDescription: "Scan the QR code with your authenticator app",
			newPassword: "New Password",
			profileInformation: "Profile information",
			profileSecurity: "Profile security",
			repeatPassword: "Repeat new password",
			tabs: {
				general: "General",
				notifications: "Notifications",
				linkedinConnections: "LinkedIn Connections",
			},
			title: "Personal Info",
			description: "Update your personal details and settings here.",
			save: "Save changes",
			saving: "Saving...",
			cancel: "Cancel",
			linkedinConnections: "LinkedIn Connections",
		},
		basics: {
			additionalInfo: "Additional information",
			question: "Question",
			measure: "Measure",
			"to-registration": "To download",
			"self-assessment": {
				yes: "Yes",
				no: "No",
			},
			yes: "Yes",
			no: "No",
			"resend-invite": "Resend invite",
			"rows-per-page": "Rows per page:",
			selectColumns: "Select columns",
			wait: "Please wait",
			reset: "Reset",
			pleaseSelect: "Please select",
			noResults: "No results",
			account: "Account",
			actions: "Actions",
			admin: "Admin",
			back: "Back",
			block: "block",
			cancel: "Cancel",
			change: "Edit",
			code: "Code",
			add: "Add",
			companyName: "Company Name",
			confirmationClose: "Are you sure you want to close the window?",
			confirm: "Confirm",
			confirmPassword: "Confirm Password",
			createSomething: "Create {{something}}",
			delete: "Delete",
			description: "Description",
			dragAndDropImage: "Drag & Drop",
			edit: "Edit",
			editSomething: "Edit {{something}}",
			email: "E-mail Address",
			enterSomething: "Enter {{something}}",
			enterFirstName: "Enter your first name",
			enterLastName: "Enter your last name",
			enterCompanyName: "Enter your company name",
			enterPassword: "Enter your password",
			enterConfirmPassword: "Confirm your password",
			firstName: "First Name",
			home: "Home",
			label: "Label",
			lastName: "Last Name",
			linkedinUrl: "LinkedIn URL",
			logout: "Logout",
			name: "Name",
			noData: "No Data",
			password: "Password",
			permissions: "Permissions",
			resetPassword: "Reset password",
			role: "Role",
			save: "Save",
			saveChanges: "Save changes",
			search: "Search {{something}}...",
			select: "Select",
			selectSomething: "Select {{something}}",
			setPassword: "Set password",
			status: "Status",
			termsAndConditions: "<noLink>I agree with the </noLink> <link1>Terms and Conditions</link1>",
			privacyStatement:
				"<noLink>I agree with the </noLink> <link1>Privacy Statement</link1><noLink>.</noLink>",
			privacyStatementObject: {
				noLink: "I agree with the ",
				link1: "Privacy Statement",
				noLink2: ".",
			},
			upload: "Upload",
			value: "Value",
			warning: "Attention",
			filter: "Filter",
			clear: "Reset All Filters",
			close: "Close",
			rows: "Rows",
			columns: "Columns",
			columnsShown: "Columns shown",
			export: "Export",
			next: "Next",
			previous: "Previous",
			addCriteria: "Add Criteria",
			copyCriteria: "Copy Criteria",
			archive: "Archive",
			unarchive: "Unarchive",
			"enter-your": "Enter your ",
			"not-support-tag": "Your browser does not support this tag.",
			thumbnail: "Thumbnail",
			"thumbnail-description": "Set a thumbnail that draws viewers' attention",
			"trailer-video": "Trailer Video",
			"trailer-video-description": "Add your video to be displayed in the training",
			"large-image": "Large Image",
			"large-image-description": "Add a long image for a banner",
			"confirm-invite-description": "To confirm the invitation, please enter your details",
			"invitation-expired": "The invitation is expired",
			"invitation-token-invalid": "Invitation token is invalid",
			"invitation-already-accepted": "Invitation has already been accepted",
			"accepting-invitation": "Accepting invitation",
			"update-pass-description": "Please choose a password that hasn’t been used before. ",
			"request-error": "Request Error",
			[Role.ADMIN]: "Admin",
			[Role.USER]: "User",
			[Role.GLOBAL_ADMIN]: "Global Admin",
			none: "None",
			"role-placeholder": "Role:",
			all: "All",
			custom: "Custom",
		},
		"manage-to-dos": {
			"select-all": "Select all",
			addAnswer: "Add answer",
			title: "Manage To-Do items",
			subtitle: "To-Do items",
			[QuestionnaireImportance.IMPORTANT]: "Important",
			[SORT_BY_ALL]: "All",
			"sort-by": "Sort by",
			"filter-by": "Filter by",
			"form-fields": {
				title: "Task name",
				category: "Category",
				requirement: "Requirement",
				description: "Description",
				recommendation: "Recommendation",
				question: "Question",
				"organization-type": "Organization type",
				"showing-mode": "Add mode",
				"out-of-date": "Out of date",
				priority: "Priority",
				"sub-tasks": "Sub-tasks",
				"add-sub-task": "Add sub-task",
				"question-description": "Question description",
			},
			[ToDoPriority.LOW]: "Low",
			[ToDoPriority.MEDIUM]: "Medium",
			[ToDoPriority.HIGH]: "High",
			"select-industries": "Industries",
			"select-industry": "select an industry",
			"select-company-balances": "Company balances",
			"select-company-balance": "select a company balance",
			"select-company-sizes": "Company Sizes",
			"select-company-size": "select a company size",
			"select-company-revenues": "Company Revenues",
			"select-company-revenue": "select a company revenue",
			[ShowingMode.ALL]: "All companies",
			[ShowingMode.ONLY_NEW]: "Only new companies",
			[ShowingMode.OUT_OF_DATE]: "Out of date",
			"about-this-task": "About this task...",
			"enter-recommendation": "Enter recommendation...",
			"delete-title": "Delete this task?",
			"delete-description": 'Are you sure want to delete task "{{taskTitle}}"?',
			"list-empty": "List is empty",
		},
		"manage-questionnaire": {
			addAnswer: "Add answer",
			addQuestion: "Add question",
			title: "Manage Questionnaire",
			questions: "Questions",
			"add-details": "Add details...",
			"form-fields": {
				title: "Task name",
				description: "Description",
				"answer-number": "Answer {{number}}",
			},
			"select-industries": "Industries",
			"select-industry": "select an industry",
			"select-company-balances": "Company balances",
			"select-company-balance": "select a company balance",
			"select-company-sizes": "Company Sizes",
			"select-company-size": "select a company size",
			"select-company-revenues": "Company Revenues",
			"select-company-revenue": "select a company revenue",
			[QuestionnaireImportance.IMPORTANT]: "Important",
			[QuestionnaireImportance.STOP]: "Exclusion",
			[QuestionnaireImportance.NONE]: "Nothing",
			"delete-title": "Delete this question?",
			"delete-description": 'Are you sure want to delete question "{{taskTitle}}"?',
			"move-to": "Move the task to step {{stepNumber}}",
			"list-empty": "The question list is empty",
		},
		"manage-industries": {
			title: "Manage Industries",
			industries: "Industries",
			"form-fields": {
				name: "Industry name",
			},
			"add-industry": "Add industry",
			"delete-title": "Delete this question?",
			"delete-description": 'Are you sure want to delete industry "{{taskTitle}}"?',
			"list-empty": "List is empty",
		},
		"manage-company-size": {
			title: "Manage Company Sizes",
			"company-sizes": "Company Sizes",
			"form-fields": {
				value: "Size Value",
			},
			"add-company-size": "Add Company Size",
			"delete-title": "Delete this size?",
			"delete-description": 'Are you sure you want to delete the size "{{sizeValue}}"?',
			"list-empty": "List is empty",
		},
		"manage-company-revenue": {
			title: "Manage Company Revenue",
			"company-revenues": "Company Revenues",
			"form-fields": {
				value: "Revenue Value",
			},
			"add-company-revenue": "Add Company Revenue",
			"delete-title": "Delete this revenue record?",
			"delete-description": 'Are you sure you want to delete the revenue "{{revenueValue}}"?',
			"list-empty": "List is empty",
		},
		"manage-company-balance": {
			title: "Manage Company Balance",
			"company-balances": "Company Balances",
			"form-fields": {
				value: "Balance Value",
			},
			"add-company-balance": "Add Company Balance",
			"delete-title": "Delete this balance?",
			"delete-description": 'Are you sure you want to delete the balance "{{balanceValue}}"?',
			"list-empty": "List is empty",
		},
		"manage-task-templates": {
			title: "Manage Task Templates",
			"add-template": "Add Template",
			"list-empty": "No task templates yet.",
			"create-title": "Create Task Template",
			"create-subtitle": "Define a new task template that will be assigned to companies.",
			"edit-title": "Edit Task Template",
			"edit-subtitle": "Update the task template details and manage sub-tasks.",
			"edit-subtask-title": "Edit Sub-task Template",
			"edit-subtask-subtitle": "Update the sub-task template details.",
			create: "Create",
			update: "Update",
			"delete-title": "Delete this template?",
			"delete-description": 'Are you sure you want to delete "{{title}}"?',
			"delete-confirm": "Delete",
			table: {
				title: "Title",
				priority: "Priority",
				order: "Order",
				subtasks: "Subtasks",
				actions: "Actions",
			},
			form: {
				title: "Title",
				description: "Description",
				priority: "Priority",
				complexity: "Complexity",
				order: "Order",
				subtasks: "Sub-task Templates",
				"new-subtask-placeholder": "New sub-task title...",
			},
		},
		errorPages: {
			buttonText: "Take Me Home",
			description:
				"You either tried some shady route or you came hee by mistake. Whichever it is, try using the navigation.",
			notFound: "404: The page you are looking for isn't here",
			serverError: "500: Internal Server Error",
			unauthorize: "401: Authorization required",
			forbidden: "403: Forbidden",
		},
		forgotPassword: {
			forgotPasswordCta: "Send reset link",
			submittedFeedbackMessage:
				"Check your inbox. We send an e-mail with instructions on how to reset your password!",
			title: "Forgot password",
			description: "Please enter your registered email address. We will send you a reset link.",
			send: "Send",
			"back-to-login": "Back to login",
		},
		inviteUser: {
			button: "Invite new user",
			modal: {
				cta: "Invite",
				emailPlaceholder: "Enter email",
				firstNamePlaceholder: "Enter first name",
				lastNamePlaceholder: "Enter last name",
				rolePlaceholder: "Select role",
				title: "Invite user",
			},
		},
		login: {
			emailPlaceholder: "Your e-mail address",
			forgotPasswordCta: "Forgot Password?",
			loginCta: "Login",
			contactUsCta: "Contact",
			passwordPlaceholder: "Your password",
			title: "Welcome back",
			description: "Welcome back! Please enter your details.",
			invalidData: "email or password wrong",
			"check-email": {
				title: "Check your email",
				description: "We sent a verification link to ",
				"back-to-login": "Back to log in",
			},
			"remember-me": "Remember me",
			noAccount: "No account yet?",
			registerCta: "Register now",
		},
		register: {
			title: "Create account",
			description: "Register to manage your NIS-2 tasks.",
			registerCta: "Register",
			haveAccount: "Already have an account? Log in",
		},
		"confirm-email": {
			title: "Email Confirmation",
			description:
				'Please click on the "Confirm" button if you want to confirm your registration on the NIS-2 platform and proceed to the list of your tasks',
			confirm: "Confirm",
			"go-to-login": "Go to login",
			"company-successfully-confirmed":
				"The company has been successfully verified, you can now log in and view the recommendations for achieving NIS-2 security level",
		},
		modals: {
			addAvatar: {
				description: "<underline>Click to upload</underline> or drag and drop",
				fileSize: "Max file size is 3 MB",
				title: "Upload File",
			},
			deleteRole: {
				title: "Delete role",
			},
			deleteUser: {
				title: "Delete user",
				description: "Are you sure you want to delete user {{firstName}} {{lastName}}?",
			},
			editUser: {
				rolePlaceholder: "Select Roles",
				title: "Edit user",
				subtitle: "Update the details as needed.",
			},
		},
		twoFA: {
			code: "Code",
			cta: "Confirm",
			title: "Enter 2FA Code",
			description: "Please enter the verification code. We have sent to your email.",
		},
		users: {
			title: "Users",
			search: "users",
		},
		userRoles: {
			title: "Roles",
			button: "User roles",
			modal: {
				create: {
					permissionPlaceholder: "Select Permissions",
					title: "Create role",
				},
				edit: {
					title: "Update role",
				},
			},
			placeholders: {
				codePlaceholder: "Enter",
				namePlaceholder: "Enter name",
			},
		},
	},
};
